import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { ShipmentHeader } from '../../../models/shipment-header';
import { selectShipmentsTableSelectedHeaders } from '../../../store/shipments-table/shipments-table.state';
import { map, take } from 'rxjs/operators';
import { ShipmentState } from '../../../utils/shipment-state';
import { ShipmentStatusEnum } from '../../../enum/shipment-status.enum';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { ConfirmService } from './confirm.service';
import { shipmentTableSetSelection } from '../../../store/shipments-table/shipments-table.actions';
import { SubmissionService } from './submission.service';
import { DocumentService } from './document.service';
import { QuotingService } from './quoting.service';
import { selectSubmitShipmentAllowed } from '../../../store/profile/profile.state';
import { PickupService } from '../../../data/pickup.service';
import { PickupBookingService } from './pickup-booking.service';
import { unique } from '../../../utils/array-reducer';
import { quotingStartMulti } from '../../../store/quote-progress/quote-progress.actions';
import { shipmentVoidMultiRequest } from '../../../store/shipment/shipment.actions';

@Injectable({
    providedIn: 'root'
})
export class ShipmentSelectionService {

    constructor(private store: Store<AppState>, private confirmService: ConfirmService, private submissionService: SubmissionService,
                private documentService: DocumentService, private quotingService: QuotingService,
                private pickupService: PickupService, private pickupBookingService: PickupBookingService) {
    }

    public pickups$: Observable<string[]> = this.store.select(selectShipmentsTableSelectedHeaders).pipe(
        map(headers => headers.filter(h => !!h.pickupId)),
        map(headers => headers.map(h => h.pickupId)),
        map(ids => unique(ids))
    );

    public cancelPickupButton$: Observable<boolean> = combineLatest([
        this.pickups$,
        this.store.select(selectSubmitShipmentAllowed)
    ]).pipe(
        map(([ selection, allowed ]) => selection.length > 0 && allowed)
    );

    public pickupShipments$: Observable<ShipmentHeader[]> = this.store.select(selectShipmentsTableSelectedHeaders).pipe(
        map(headers => headers.filter(h => !!h.pickupRequired))
    );

    public pickupSelectionButton$: Observable<boolean> = combineLatest([
        this.pickupShipments$,
        this.store.select(selectSubmitShipmentAllowed)
    ]).pipe(
        map(([ selection, allowed ]) => selection.length > 0 && allowed)
    );

    public voidableShipments$: Observable<ShipmentHeader[]> = this.store.select(selectShipmentsTableSelectedHeaders).pipe(
        map(headers => headers.filter(h => ShipmentState.isVoidable(h.status)))
    );

    public voidSelectionButton$: Observable<boolean> = combineLatest([
        this.voidableShipments$,
        this.store.select(selectSubmitShipmentAllowed)
    ]).pipe(
        map(([ selection, allowed ]) => selection.length > 0 && allowed)
    );

    public shippableShipments$: Observable<ShipmentHeader[]> = this.store.select(selectShipmentsTableSelectedHeaders).pipe(
        map(headers => headers.filter(h => h.status === ShipmentStatusEnum.QUOTED && !!h.serviceId)),
    );

    public shipSelectionButton$: Observable<boolean> = combineLatest([
        this.shippableShipments$,
        this.store.select(selectSubmitShipmentAllowed)
    ]).pipe(
        map(([ selection, allowed ]) => selection.length > 0 && allowed)
    );

    public printableShipments$: Observable<ShipmentHeader[]> = this.store.select(selectShipmentsTableSelectedHeaders).pipe(
        map(headers => headers.filter(h => ShipmentState.isSubmitted(h.status)))
    );

    public printSelectionButton$: Observable<boolean> = this.printableShipments$.pipe(
        map(selection => selection.length > 0)
    );

    public quotableShipments$: Observable<ShipmentHeader[]> = this.store.select(selectShipmentsTableSelectedHeaders).pipe(
        map(headers => headers.filter(h => ShipmentState.isQuotable(h.status)))
    );

    public quoteSelectionButton$: Observable<boolean> = this.quotableShipments$.pipe(
        map(selection => selection.length > 0)
    );

    public selectionButtonCount$: Observable<number> = combineLatest([
        this.printSelectionButton$,
        this.quoteSelectionButton$,
        this.voidSelectionButton$,
        this.shipSelectionButton$
    ]).pipe(
        map((buttons) => buttons.filter(b => !!b)),
        map((buttons) => buttons.length)
    );

    public voidSelection(): void {
        this.voidableShipments$.pipe(take(1)).subscribe(selection => {
            const count = selection.length;
            if (count > 0) {
                const title = count === 1 ? $localize`Void Shipment` : $localize`Void ${count} Shipments`;
                const text = count === 1 ? $localize`Cancel this shipment with the carrier?  Once voided this shipment cannot be used.` :
                    $localize`Cancel the selected shipments with the carriers?  Once voided these shipments cannot be used.`;
                this.confirmService.confirm(title, text, () => {
                    //this.submissionService.voidMulti(selection.map(h => h.id));
                    this.store.dispatch(shipmentVoidMultiRequest({ shipmentIds: selection.map(h => h.id) }));
                    this.store.dispatch(shipmentTableSetSelection({ ids: [] }));
                });
            }
        });
    }

    public submitSelection(): void {
        this.shippableShipments$.pipe(take(1)).subscribe(selection => {
            this.submissionService.submitShipmentMulti(selection.map(h => h.id));
            this.store.dispatch(shipmentTableSetSelection({ ids: [] }));
        });
    }

    public printSelection(): void {
        this.printableShipments$.pipe(take(1)).subscribe(selection => {
            this.documentService.openShipmentWaybills(selection.map(h => h.id));
        });
    }

    public quoteSelection(): void {
        this.store.dispatch(quotingStartMulti());
        this.store.dispatch(shipmentTableSetSelection({ ids: [] }));
    }

    public pickupSelection(): void {
        this.pickupShipments$.pipe(take(1)).pipe(
            map(selection => selection.map(s => s.id))
        ).subscribe(selection => {
            this.pickupBookingService.openPickupBookingShipments(selection);
        });
    }

    public pickupCancelSelection(): void {
        this.pickups$.pipe(take(1)).subscribe(selection => {
            this.pickupBookingService.openPickupCancel(selection);
        });
    }

}
